// @ts-check
/**
 * Two fixes for the Wishlist by Square app's own product cards, on the app's
 * `/apps/page/wishlist` page — see the "Wishlist app" note in CLAUDE.md and the matching CSS in
 * snippets/wishlist-app-card-styles.liquid:
 *
 * 1. Positions the "Add to cart" button (and whatever the app swaps in for its own success
 *    state — see below) over the bottom-right corner of the product photo.
 * 2. Forces the photo itself to fill its (now fixed-aspect-ratio, see the CSS) wrapper box.
 *
 * ⚠️ The first version of (1) *moved* the button into `.Vtl-WishlistProduct__ImageWrapper` via
 * `Node.append()` so plain CSS `position: absolute` could anchor to the image box directly. That
 * broke the button: ImageWrapper sits inside `.Vtl-WishlistProduct__Link`, an `<a>` — a `<button>`
 * nested inside an `<a>` is invalid HTML, and it stopped responding to clicks entirely. This
 * version never moves anything. The button stays exactly where the app renders it (a sibling of
 * `.Link`, both children of `.Vtl-WishlistProduct`, which the accompanying CSS makes
 * `position: relative`); this script only measures the image wrapper's own rendered height and
 * writes that as the (each) overlay element's inline `top`, so `position: absolute; right: …` in
 * CSS lands it over the image without ever touching the DOM tree the app owns.
 *
 * ⚠️ **The overlay selector matches by position, not by tag or class, and that's deliberate.**
 * The app replaces (or, possibly, adds alongside) the plain "Add to cart" `<button>` with a
 * different, plain-text "Added to cart" confirmation element after a successful add — confirmed
 * live, and it is NOT a `<button>`, so an earlier version of this file that only ever looked for
 * `:scope > button` missed it entirely, leaving that confirmation text visible in normal
 * document flow instead of hidden/positioned like the button. The selector below instead matches
 * *any* direct child of `.Vtl-WishlistProduct` that isn't `.Link` (the product image/title/price)
 * or `.CloseButton` (the "X") — whichever of "Add to cart" / "Added to cart" exists at a given
 * moment, in whatever element the app chooses to render it as, gets the same treatment. If both
 * somehow exist at once, they simply render on top of each other at the same spot — no visible
 * difference, since both end up identically sized, filled, and text-hidden.
 *
 * (2) exists because no CSS rule — not even `!important`, not even a catch-all `* { }` inside the
 * wrapper — made the photo stop rendering at its own short intrinsic size. The only thing that
 * beats CSS `!important` is an inline `style` attribute that *also* carries `!important`, which
 * genuinely cannot be out-specified by any external rule; the only way to still win is to not
 * compete with it at all, and instead write the exact same style property on the exact same
 * element from JS — there being only one `style` attribute per element, this simply replaces
 * whatever the app put there rather than fighting it in the cascade.
 *
 * A ResizeObserver per image wrapper re-applies both fixes on resize — the wrapper is a fluid
 * percentage-width, fixed-aspect-ratio box, so its height (and each overlay's `top`) changes with
 * viewport width, and the app may re-assert its own inline sizing on the image at any time.
 *
 * ⚠️ **A `CartLinesUpdateEvent`-dispatching bridge (so this button's click would show this
 * theme's own floating "Added to cart" notification, sections/cart-added-notification.liquid,
 * like every other add-to-cart button on the site) was built, shipped, and then removed.** Right
 * after a successful add, the app navigates the whole page to `/cart` itself — cutting off the
 * notification the instant it appears, before a shopper could ever actually see it. That redirect
 * is a **direct `window.location` assignment**, confirmed by testing a capture-phase
 * `document` click interceptor (cancelling only `event.isTrusted === false` clicks, so a real
 * person's own "view cart" clicks elsewhere would have been unaffected) that had no effect at
 * all — a direct assignment fires no cancelable DOM event, which is a browser security boundary,
 * not a gap in theme code, and is not fixable from here. Once the notification could never
 * usefully be seen, dispatching it was pure noise (worse, the notification's own auto-close timer
 * and animations would begin, then instantly get destroyed by the navigation) — the merchant
 * asked for it to be turned off here specifically for that reason. If the redirect is ever
 * disabled (an app setting, or Square Apps support), reintroduce the bridge from git history
 * rather than rebuilding it — it dispatched a real `CartLinesUpdateEvent` with `context: 'product'`
 * off the app's own `/cart/add.js` response, the same way assets/buy-x-get-y.js dispatches one
 * off its own cart-modifying fetch call.
 *
 * Not a @theme/component: unlike assets/app-element-slot.js, there is no theme-rendered host
 * element to attach one to — `.Vtl-WishlistPage__Products` and everything inside it is rendered
 * entirely by the app's own JS, an unknown number of times, at an unknown moment. This is a
 * plain, self-starting observer instead.
 */

const PRODUCTS_SELECTOR = '.Vtl-WishlistPage__Products';
const ITEM_SELECTOR = '.Vtl-WishlistProduct';
const IMAGE_WRAPPER_SELECTOR = '.Vtl-WishlistProduct__ImageWrapper';
// See the file doc's "overlay selector matches by position" note above for why this isn't
// `.Vtl-WishlistProduct__AddToCart` (or `:scope > button`).
const OVERLAY_SELECTOR = ':scope > *:not(.Vtl-WishlistProduct__Link):not(.Vtl-WishlistProduct__CloseButton)';
/** How far up from the image's own bottom edge each overlay element's centre should sit, in px. */
const BOTTOM_OFFSET = 10;

/** Tracks image wrappers already being watched, so a re-run of processAll() never double-observes one. */
const observedWrappers = new WeakSet();

/**
 * @param {HTMLElement} imageWrapper
 * @param {HTMLElement} overlay
 */
function positionOverlay(imageWrapper, overlay) {
  const height = imageWrapper.offsetHeight;
  if (height === 0) return; // Not laid out yet — the next resize/mutation pass will retry.
  overlay.style.top = `${height - overlay.offsetHeight - BOTTOM_OFFSET}px`;
}

/**
 * Forces every image inside the wrapper to fill it, by setting the exact same style properties
 * the app itself may have set inline (with its own `!important`) — see the file doc for why this
 * has to be a same-element overwrite rather than a competing CSS rule.
 * @param {HTMLElement} imageWrapper
 */
function fillImage(imageWrapper) {
  imageWrapper.querySelectorAll('img').forEach((img) => {
    // The actual, DevTools-confirmed root cause: the app's own `.Vtl-WishlistProduct__Image`
    // rule sets `max-height: 250px` (its `height`/`width`/`object-fit` were already losing to
    // this script's earlier fixes, but `max-height` is a distinct property — nothing here had
    // touched it yet, so it kept clamping the rendered image regardless of what `height`
    // computed to). `position: absolute` + `inset: 0` also sidesteps a separate, real ambiguity:
    // the wrapper's height comes from `aspect-ratio` (snippets/wishlist-app-card-styles.liquid),
    // which isn't reliably treated as a "definite" height for a child's percentage `height: 100%`
    // to resolve against in every browser — absolute positioning fills the nearest positioned
    // ancestor's actual laid-out box instead of running that percentage calculation at all.
    img.style.setProperty('position', 'absolute', 'important');
    img.style.setProperty('inset', '0', 'important');
    img.style.setProperty('width', '100%', 'important');
    img.style.setProperty('height', '100%', 'important');
    img.style.setProperty('max-height', 'none', 'important');
    img.style.setProperty('max-width', 'none', 'important');
    img.style.setProperty('object-fit', 'cover', 'important');
  });
}

/** @param {Element} item */
function wireItem(item) {
  const imageWrapper = /** @type {HTMLElement | null} */ (item.querySelector(IMAGE_WRAPPER_SELECTOR));
  if (!imageWrapper) return;

  fillImage(imageWrapper);

  const overlays = /** @type {NodeListOf<HTMLElement>} */ (item.querySelectorAll(OVERLAY_SELECTOR));
  overlays.forEach((overlay) => positionOverlay(imageWrapper, overlay));

  if (observedWrappers.has(imageWrapper)) return;
  observedWrappers.add(imageWrapper);
  new ResizeObserver(() => {
    fillImage(imageWrapper);
    item.querySelectorAll(OVERLAY_SELECTOR).forEach((overlay) => positionOverlay(imageWrapper, /** @type {HTMLElement} */ (overlay)));
  }).observe(imageWrapper);
}

/** @param {ParentNode} root */
function processAll(root) {
  root.querySelectorAll(ITEM_SELECTOR).forEach(wireItem);
}

/** @param {Element} products */
function watchProducts(products) {
  processAll(products);
  // Vitals re-renders items (quantity/cart state changes can replace nodes outright, including
  // swapping "Add to cart" for its "Added to cart" confirmation — see the file doc); re-running
  // on every mutation keeps a freshly-rendered item's overlay(s) positioned too.
  new MutationObserver(() => processAll(products)).observe(products, { childList: true, subtree: true });
}

/**
 * The grid only exists on the app's own page, but this script is loaded on every page (Liquid cannot
 * tell which page the app proxy is rendering). Everywhere else the whole-document observer below
 * waited for a node that never appears — one callback, and a full-document `querySelector`, for every
 * DOM mutation of the whole visit: the announcement bar's clock, Vitals injecting a heart into each
 * product card, Clarity, carousels. PageSpeed listed it as a first-party script in "Reduce JavaScript
 * execution time" (64 ms) and it adds main-thread work to every interaction.
 *
 * A URL that mentions "wishlist" still watches until the grid shows up, as before. Any other page
 * gives up after WATCH_OTHER_PAGES_MS — more than enough for the app to render its grid under a
 * differently named page, and a few seconds of watching instead of an endless one everywhere else.
 */
const WISHLIST_PATH = /wishlist/i;
const WATCH_OTHER_PAGES_MS = 10000;

const existingProducts = document.querySelector(PRODUCTS_SELECTOR);
if (existingProducts) {
  watchProducts(existingProducts);
} else {
  // The wishlist grid doesn't exist yet — Vitals renders it after its own bundle loads. Watch the
  // whole page just until it shows up, then narrow down to observing only that grid.
  const bodyObserver = new MutationObserver(() => {
    const products = document.querySelector(PRODUCTS_SELECTOR);
    if (products) {
      bodyObserver.disconnect();
      watchProducts(products);
    }
  });
  bodyObserver.observe(document.body, { childList: true, subtree: true });

  if (!WISHLIST_PATH.test(location.pathname)) {
    setTimeout(() => bodyObserver.disconnect(), WATCH_OTHER_PAGES_MS);
  }
}
